import {
  Button,
  EllipsisSpan,
  FormContent,
  PopConfirm,
  Table,
  Tag,
  useBreadcrumbRoutes,
  useUpdate,
  Wrapper,
} from '@maxtropy/components';
import React, { useState } from 'react';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2MicrogridResolvedGetStrategyPagePost,
  V2MicrogridResolvedGetStrategyPagePostResponse,
  apiV2MicrogridResolvedDelStrategyPost,
  apiV2MicrogridResolvedChangeStrategyStatusPost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {  StrategyTypeEnum, StrategyTypeEnumDisplay } from './util';
import AlgorithmConfigurationModal from './components/algorithmConfigurationModal';
import CreateStrategyModal from './components/createStrategyModal';

const columns = [
  {
    title: '策略名称',
    dataIndex: 'strategyName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '策略类型',
    dataIndex: 'strategyType',
    ellipsis: { showTitle: true },
    render: (v: StrategyTypeEnum) => <EllipsisSpan value={v && StrategyTypeEnumDisplay[v]} />,
  },
  {
    title: '应用状态',
    dataIndex: 'enabled',
    ellipsis: { showTitle: true },
    render: (v: boolean) => (
      <EllipsisSpan
        value={
          <Tag border="solid" color={v ? 'success' : 'invalid'}>
            {v ? '应用中' : '未应用'}
          </Tag>
        }
      />
    ),
  },
  {
    title: '最后操作时间',
    dataIndex: 'lastUpdateTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'lastUpdateUserName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

export type itemProps = Exclude<V2MicrogridResolvedGetStrategyPagePostResponse['list'], undefined>[number];

const Configuration = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const id = urlSearchParams.get('id');
  
  const [update, updateFn] = useUpdate();
  const [openConfigModal, setOpenConfigModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [strategyId, setStrategyId] = useState<number>();
  // 查询列表
  const { data: pageList, loading } = useRequest(
    async () => {
      const res = await apiV2MicrogridResolvedGetStrategyPagePost(
        {
          configId: id ? Number(id) : undefined,
          // 此处产品未要求做分页
          page:1,
          size: 499,
        },
      );
      return res?.list ?? [];
    },
    {
      refreshDeps: [id, update],
    }
  );

  // 删除
  const onDelete = (strategyId?: number) => {
    apiV2MicrogridResolvedDelStrategyPost(
      {
        strategyId,
      },
    ).then(_ => {
      updateFn();
    });
  };
  // 修改状态
  const onStatus = (strategyId?: number, enabled?: boolean) => {
    apiV2MicrogridResolvedChangeStrategyStatusPost(
      {
        strategyId,
        enabled: !enabled,
      },
    ).then(_ => {
      updateFn();
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width:240,
      render: (_: any, record: itemProps) => {
        return (
          <Space size={10}>
            {record.enabled ? (
              <PopConfirm
                title="是否停止应用该策略？"
                onConfirm={() => {
                  onStatus(record.strategyId, record.enabled);
                }}
              >
                <Button type="link">停止应用</Button>
              </PopConfirm>
            ) : (
              <PopConfirm
                title="是否应用该策略？应用该策略将支持微电网下调度。"
                onConfirm={() => {
                  onStatus(record.strategyId, record.enabled);
                }}
              >
                <Button type="link">应用</Button>
              </PopConfirm>
            )}
            <Button
              type="link"
              onClick={() => {
                setOpenCreateModal(true);
                setStrategyId(record.strategyId);
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              onClick={() => {
                setOpenConfigModal(true);
                setStrategyId(record.strategyId);
              }}
            >
              算法运行配置
            </Button>
            <PopConfirm
              title="是否删除该策略？删除后无法恢复。"
              onConfirm={() => {
                onDelete(record.strategyId);
              }}
            >
              <Button type="link" danger disabled={record.enabled}>
                删除
              </Button>
            </PopConfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '策略配置' }]}>
      <FormContent narrow title="策略配置" style={{ padding: 0 }}>
        <Space size={10} style={{ marginBottom: 10 }}>
          <Button type="primary" onClick={() => setOpenCreateModal(true)}>
            <PlusOutlined />
            新建策略
          </Button>
        </Space>
        <Table
          rowKey="id"
          sticky
          loading={loading}
          columns={buildColumns}
          dataSource={pageList ?? []}
          pagination={false}
        />
      </FormContent>
      {openConfigModal && (
        <AlgorithmConfigurationModal
          strategyId={strategyId}
          onConfirm={() => {
            setOpenConfigModal(false);
            setStrategyId(undefined);
            updateFn();
          }}
          onCancel={() => {
            setOpenConfigModal(false);
            setStrategyId(undefined);
          }}
        />
      )}
      {openCreateModal && (
        <CreateStrategyModal
          strategyId={strategyId}
          configId={id!}
          onConfirm={() => {
            setOpenCreateModal(false);
            setStrategyId(undefined);
            updateFn();
          }}
          onCancel={() => {
            setOpenCreateModal(false);
            setStrategyId(undefined);
          }}
        />
      )}
      <Space size={8} className="sticky-footer">
        <Button onClick={() => navigate(`/microgrid/strategy/management`)}>返回</Button>
      </Space>
    </Wrapper>
  );
};

export default Configuration;
